import { action, computed, observable, runInAction } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { createSectionListGroups } from '../../lib/utils'
import analytics from '../../../src/stores/analytics'
import { DeleteUserMutation, MarketplaceListingQuery, SaasSettingsFieldsFragment } from '../../../graphQL/generated/client'
import { AutoAcceptBookingRequestOption, LoyaltySchemeUsageType, StripeAccountStatus } from '../../../graphQL/generated/enums'
import moment from 'moment'
import { MIN_BOOKING_AMOUNT, MIN_BOOKING_DATE, MOBILE_AVAILABILITY_ID, PACKDOWN_TIME, TRAVEL_TIME } from '../../../shared-lib/commonStrings'
import { defaultCountry, defaultCurrency } from '../../../shared-lib/i18n/i18n'
import { defaultProThemeSettings } from '../../../shared-lib/proTheme/proTheme'
import { getSearchConfig } from '../../lib/AddressMatch'
import { $RootStore } from '../../../src/stores/RootStore'
import { $Postcode } from '../PostcodeStore/PostcodeModel'
import { $Treatment } from '../BookingStore/bookingHelpers'
import { countryTimezones } from '../../../shared-lib/timezones'
import listeners from '../../communication/Listeners'
import { scopeData } from '../../lib/scopeData'
import { SalonMembership } from '../SalonStore/SalonStore'
import { WorkplaceBusinessDetails } from '../../lib/workplaces'

export type $BookingLocation = {
  _id: string
  name: string
  detail: string
  enabled: boolean
  addressId?: string
}

export type GoogleServices = 'Business' | 'Calendar'

// Mirrors DEFAULT_TAP_TO_PAY_LIMIT in the API (minor units)
export const DEFAULT_TAP_TO_PAY_LIMIT = 50000

export interface ProInformation {
  status?: string
  city?: string
  verifiedDate?: Date
}

interface $SaasSettings extends SaasSettingsFieldsFragment {
  branding: Record<string, any> | undefined
  info: Array<{ title: string, body: string }>
}

export interface $ProfessionalProfile {
  bio: string
  favouriteCount: number
  reviewCount: number
  averageReview: number
  regions: Array<string>
  treatmentIds: Array<string>
  postcodeIds: Array<string>
  treatments: Array<$Treatment>
  postcodes: Array<$Postcode>
  defaultTransportMode?: string
  numberOfBookings: number
  information?: ProInformation
  createdAt: Date
  genderPreference: string
  saasProfileUrl: string
  saasSettings: $SaasSettings
  notificationPreferences: ProfessionalNotificationPreferences
  externalReviewCount: number
  averageExternalReview: number
  completedSetupSteps: Array<string>
  completedSetupTasks: Array<string>
  calendarColors?: CalendarColors
  giftCardSettings?: GiftCardSettings
}

export interface ProfessionalNotificationPreferences {
  privateBookingsTomorrowSummary: boolean
  privateBookingsTodaySummary: boolean
}
export interface $User {
  emailConfirmed?: boolean
  status?: string
  mobileConfirmed?: boolean
  _id: string
  role?: string
  guestKey?: string
  emailAddress: string
  firstName: string
  lastName: string
  mobile: string
  gender?: string
  genderSelfDescription?: string
  professionalProfile: $ProfessionalProfile
  stripeConnectedAccount: string
  stripePayoutEnabled: boolean
  stripeDashboard: string
  stripeAccountStatus: StripeAccountStatus
  soloServiceFee?: number
  acceptedTermsPA: boolean
  lastHealthPPECheck: Date | null
  healthPPEConfirmed: boolean
  defaultTransportMode?: string
  profileUrl: string
  blocked: boolean
  tags: Array<string>
  stripeCapabilities: StripeCapabilities
  stripeBusinessName: string
  stripeDescriptor: string
  stripePayoutInterval?: string
  stripeLimits?: StripeLimits
  acceptedTermsSaaS?: boolean
  subscriptionDetails?: SoloSubscriptionDetails
  attributionData?: Record<string, string>
  createdAt?: Date
  referralCode?: string
  referredBy?: string
  soloReferralId?: string
  currencyCode?: SupportedCurrency
  countryISOCode?: SupportedCountry
  timezone?: string
  googleBusinessAuth?: GoogleAuthBusiness
  googleCalendarAuth?: GoogleAuthCalendar
}

const userDefaults: $User = {
  _id: '',
  emailConfirmed: false,
  status: '',
  mobileConfirmed: false,
  role: '',
  guestKey: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  tags: [],
  mobile: '',
  gender: '',
  genderSelfDescription: undefined,
  professionalProfile: {
    bio: '',
    favouriteCount: 0,
    reviewCount: 0,
    averageReview: 0,
    regions: [],
    treatmentIds: [],
    postcodeIds: [],
    treatments: [],
    postcodes: [],
    defaultTransportMode: '',
    numberOfBookings: 0,
    createdAt: new Date(),
    genderPreference: 'none',
    saasProfileUrl: '',
    information: {},
    saasSettings: {
      branding: {},
      info: [],
      themeSettings: {
        colorPrimary: '',
        colorSecondary: '',
        colorNeutralLight: '',
        colorNeutralDark: '',
        cornerStyle: '',
        shadowStyle: '',
        titlesFont: '',
        titlesMotif: '',
        bodyFont: '',
      },
    },
    notificationPreferences: {
      privateBookingsTomorrowSummary: true,
      privateBookingsTodaySummary: true,
    },
    externalReviewCount: 0,
    averageExternalReview: 0,
    completedSetupSteps: [],
    completedSetupTasks: [],
  },
  stripeConnectedAccount: '',
  stripePayoutEnabled: false,
  stripeAccountStatus: StripeAccountStatus.NotSubmitted,
  stripeDashboard: '',
  acceptedTermsPA: false,
  acceptedTermsSaaS: false,
  lastHealthPPECheck: null,
  healthPPEConfirmed: false,
  profileUrl: '',
  blocked: false,
  stripeCapabilities: {},
  stripeBusinessName: '',
  stripeDescriptor: '',
  subscriptionDetails: {},
}

interface StaticFormData {
  enabled: boolean
  name: string
  hideFullAddress: boolean
  externalAddressId?: string
  summary: string
  minBookingAmount: number
  minBookingDate: number
  defaultPackdownTime: number
  defaultAvailabilityRange: number
  requestsBlockCalendar?: boolean
  timeSlotInterval: number
  autoAcceptBookingRequests: AutoAcceptBookingRequestOption
}

export const staticLocationDefaults: StaticFormData = {
  enabled: true,
  name: 'Salon',
  summary: '',
  externalAddressId: '',
  hideFullAddress: false,
  minBookingAmount: MIN_BOOKING_AMOUNT,
  minBookingDate: MIN_BOOKING_DATE,
  defaultPackdownTime: PACKDOWN_TIME,
  defaultAvailabilityRange: 365,
  requestsBlockCalendar: false,
  timeSlotInterval: 15,
  autoAcceptBookingRequests: AutoAcceptBookingRequestOption.Off,
}

interface MobileFormData {
  enabled: boolean
  fee: number
  travelDistanceMiles: number
  externalAddressId?: string
  summary: string
  minBookingAmount: number
  minBookingDate: number
  defaultPackdownTime: number
  useTravelTime: boolean
  defaultTravelTime: number
  defaultAvailabilityRange: number
  requestsBlockCalendar?: boolean
  timeSlotInterval: number
  autoAcceptBookingRequests: AutoAcceptBookingRequestOption
  requestParkingInformation: boolean
}

export const mobileLocationDefaults: MobileFormData = {
  enabled: false,
  fee: 0,
  travelDistanceMiles: 1,
  externalAddressId: undefined,
  summary: '',
  minBookingAmount: MIN_BOOKING_AMOUNT,
  minBookingDate: MIN_BOOKING_DATE,
  defaultPackdownTime: PACKDOWN_TIME,
  defaultTravelTime: TRAVEL_TIME,
  useTravelTime: false,
  defaultAvailabilityRange: 365,
  requestsBlockCalendar: false,
  timeSlotInterval: 15,
  autoAcceptBookingRequests: AutoAcceptBookingRequestOption.Off,
  requestParkingInformation: true,
}

export default class UserStore extends ModelStore {
  @observable accessor data: $User = { ...userDefaults }
  @observable accessor profilePictureUpdate = Date.now()
  @observable accessor marketplaceListing: MarketplaceListingQuery['marketplaceListing']
  @observable accessor loyaltySchemes: LoyaltyScheme[] | undefined = undefined
  nullableKeys = ['staticLocation', 'googleBusinessAuth', 'googleCalendarAuth']
  fetchMethodName = 'getProfessional'
  userUpdatedSubscription?: { unsubscribe: () => void }

  constructor(data: Partial<$User>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  clearUser() {
    this.merge({ ...userDefaults })
  }

  // Clear all data (used for the logout)
  @action.bound clearData(): void {
    this.stopListeningOnUserUpdated()
    this.data = userDefaults
    this.syncInStorage()
  }

  @action.bound stopListeningOnUserUpdated(): void {
    this.userUpdatedSubscription?.unsubscribe()
    this.userUpdatedSubscription = undefined
  }

  @action.bound async listenOnUserUpdated_api() {
    const { communication: { client }, dropdownMessageHandler, i18n: { s } } = this.rootStore
    const { merge } = this

    // The subscription is bound to the id of whoever was signed in when it opened, so an account
    // switch must drop it — otherwise the previous account's updates merge into this store.
    this.stopListeningOnUserUpdated()

    this.userUpdatedSubscription = client.subscribe<{ userUpdated?: Record<string, unknown> }>({
      query: listeners.userUpdated,
      variables: { userId: this.data._id },
    }).subscribe({
      next(res) {
        const data = res.data?.userUpdated
        if (data) merge(data)
      },
      error(err: any) {
        console.error(err)
        dropdownMessageHandler.showDropdownMessage({
          title: s('There was an issue fetching the latest stripe data. Please refresh.'),
          type: 'error',
          closeInterval: 1000,
        })
      },
    })

    return this.userUpdatedSubscription
  }

  @computed get availableTreatmentGroups() {
    return createSectionListGroups(this.data.professionalProfile.treatments, 'type')
  }

  @computed get availableLocalityGroups() {
    return createSectionListGroups(this.data.professionalProfile.postcodes, 'locality')
  }

  @action.bound async goToSetDefaultAvailability(isOnboarding = false) {
    const { navigationStore, professionalAvailabilityStore } = this.rootStore

    await this.fetch_api()

    const { professionalProfile } = this.data

    const tempAvailablity = {
      status: 0,
      day: '',
      times: [],
      workingTimes: [],
      treatmentIds: professionalProfile.treatmentIds,
      postcodeIds: professionalProfile.postcodeIds,
    }

    professionalAvailabilityStore.setEditingAvailability(tempAvailablity)
    navigationStore.navigate('AreaAvailability', {
      mode: 'selectDefaults',
      isOnboarding: isOnboarding,
    })
  }

  @action.bound setDefaultAvailability(postcodeIds: Array<string>, treatmentIds: Array<string>) {
    const { professionalProfile } = this.data
    professionalProfile.postcodeIds = postcodeIds
    professionalProfile.treatmentIds = treatmentIds
    this.syncInStorage()
  }

  @action.bound async getProfessionalAvailabilityData_api(): Promise<any> {
    const { communication: { requester } } = this.rootStore

    try {
      const response = await requester.getProfessionalAvailabilityData({ _id: this.data._id })
      const data = response.getProfessional || {}
      this.merge(data)
    } catch (e) {
      return null
    }
  }

  @action.bound async getProfessionalWithStripe_api(): Promise<any> {
    const { communication: { requester } } = this.rootStore

    runInAction(() => {
      this.loading = true
    })

    try {
      const response = await requester.getProfessionalWithStripe({ _id: this.data._id })
      const data = response.getProfessional || {}
      this.merge(data)
    } catch (e) {
      return null
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async updateSaasUser_api(data: UpdateSaasUserInput) {
    const { communication: { requester } } = this.rootStore

    try {
      await requester.updateSaasUser({ data: { ...data } })
      return true
    } catch (e) {
      return false
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async createSoloBusiness_api(details: WorkplaceBusinessDetails): Promise<boolean> {
    const { communication: { requester }, authStore, subscriptionStore, errorHandler } = this.rootStore

    try {
      const { createSoloBusiness } = await requester.createSoloBusiness(details)

      this.merge(createSoloBusiness)
      authStore.applyUserLocale(createSoloBusiness)
      subscriptionStore.syncWithChargebee()
      await this.updateUserTraits_api()
      return true
    } catch (e: any) {
      errorHandler.showErrorInUI(e)
      return false
    }
  }

  @action.bound async updateProfessionalProfile_api(data: UserProfessionalProfileInput) {
    const { communication: { requester } } = this.rootStore

    try {
      runInAction(() => {
        this.loading = true
      })

      const { updateProfessionalProfile } = await requester.updateProfessionalProfile({ data: data })

      this.merge({ professionalProfile: updateProfessionalProfile })
      return true
    } catch (e) {
      return null
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async updateSaasSettings_api(data: SaasSettingsInput) {
    const saasSettings = {
      ...data,
    }
    return this.updateProfessionalProfile_api({ saasSettings })
  }

  @action.bound async updateWebsiteTheme_api(data: Partial<ProThemeSettings>) {
    const { dropdownMessageHandler, i18n: { s } } = this.rootStore

    const themeSettings = {
      ...defaultProThemeSettings,
      ...this.data.professionalProfile.saasSettings.themeSettings,
      ...data,
    }

    const success = await this.updateProfessionalProfile_api({ saasSettings: { themeSettings } })

    if (success) {
      dropdownMessageHandler.showDropdownMessage({
        type: 'success',
        title: s('Success'),
        message: s('Your changes have been saved'),
      })
      return true
    }

    dropdownMessageHandler.showDropdownMessage({
      type: 'error',
      title: s('Error'),
      message: s('There was an issue saving your changes. Please try again'),
    })
    return false
  }

  @action.bound async requestProfessionalPaymentCapability_api() {
    const { communication: { requester } } = this.rootStore

    try {
      runInAction(() => {
        this.loading = true
      })

      const response = await requester.requestProfessionalPaymentCapability({})

      return response.requestProfessionalPaymentCapability
    } catch (e) {
      return false
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound async deleteUser_api(): Promise<DeleteUserMutation['deleteUser']> {
    const { communication: { requester } } = this.rootStore

    try {
      const { deleteUser } = await requester.deleteUser()
      return deleteUser
    } catch (e) {
      return false
    }
  }

  sendPreviewEmail_api = async (eventName: string) => {
    const { communication: { requester } } = this.rootStore

    try {
      const { sendPreviewEmail } = await requester.sendPreviewEmail({ eventName })
      return sendPreviewEmail
    } catch (e) {
      return false
    }
  }

  updateUserTraits_api = async () => {
    const { communication: { requester }, i18n, config, authStore } = this.rootStore

    let traits: Record<string, any> = {}

    try {
      const [response] = await Promise.all([
        authStore.isReceptionSession ? null : requester.getUserTraits({}),
        this.fetch_api(),
      ])
      traits = { ...response?.getUserTraits }
    } catch (e) {
      traits = {}
    }

    this.syncInStorage()

    traits.daysSinceSignUp = this.daysSinceSignUp || 0

    const newCurrency = this.data?.currencyCode ?? defaultCurrency
    i18n.setCurrency(newCurrency)
    analytics.config.currency = newCurrency
    i18n.setCountry(this.data?.countryISOCode ?? defaultCountry)
    runInAction(() => {
      config.addressSearchCountry = getSearchConfig(this.data?.countryISOCode)
    })

    analytics.setUserTraits(traits)
    analytics.identify('PROFESSIONAL', this.data)
  }

  enrollReferralProgram_api = async () => {
    const { communication: { requester } } = this.rootStore

    analytics.track('Referral Program Enrollment Requested')

    try {
      runInAction(() => {
        this.loading = true
      })

      const { enrollReferralProgram } = await requester.enrollReferralProgram()

      if (enrollReferralProgram) {
        this.merge(enrollReferralProgram)
      }

      return true
    } catch (e) {
      analytics.track('Referral Program Enrollment Failed', { error: e })
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }

    return false
  }

  getSoloReferrals_api = async () => {
    const { communication: { requester } } = this.rootStore

    try {
      runInAction(() => {
        this.loading = true
      })

      const { getSoloReferrals } = await requester.getSoloReferrals()

      if (getSoloReferrals) {
        return getSoloReferrals
      }
    } catch (e) {
      // ???
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }

    return []
  }

  fetchMarketplaceListing = async () => {
    const { marketplaceListing } = await this.rootStore.communication.requester.marketplaceListing()
    runInAction(() => {
      if (marketplaceListing) this.marketplaceListing = marketplaceListing
    })
  }

  @computed get brandMarketplace() {
    return this.marketplaceListing?.tags?.filter(t => t.marketplaceSlug !== 'solo' && t.category === 'Verification')[0]
  }

  @computed get isTradeTesting() {
    return this.data.professionalProfile.information?.status === 'trade-test'
  }

  @computed get onlinePaymentsEnabled() {
    return this.data.stripeCapabilities.card_payments === 'active'
  }

  @computed get payoutsFrozen() {
    return this.data.stripePayoutInterval === 'manual'
  }

  @computed get tapToPayLimit() {
    return this.data.stripeLimits?.tapToPay ?? DEFAULT_TAP_TO_PAY_LIMIT
  }

  @computed get termsToAccept() {
    if (!this.data.acceptedTermsSaaS) return 'TERMS_SOLO'
    if (!this.rootStore.authStore.isSaasUser && !this.data.acceptedTermsPA) return 'TERMS_PA'
    return undefined
  }

  @computed get staticLocations() {
    return this.data.professionalProfile.saasSettings.staticLocations ?? []
  }

  @computed get mobileLocation() {
    return this.data.professionalProfile.saasSettings.mobileLocation ?? undefined
  }

  @computed get validLocations() {
    const { salonStore } = this.rootStore

    return this.transformLocations(
      this.mobileLocation,
      this.staticLocations,
      salonStore.mySalonMemberships,
      this.rootStore.externalAddressStore.items.map(i => i.data),
    )
  }

  @computed get validLocationsWithBookingPermission() {
    const { salonStore } = this.rootStore

    return this.transformLocations(
      this.mobileLocation,
      this.staticLocations,
      salonStore.bookableSalonMemberships, // Only include memberships with booking permission
      this.rootStore.externalAddressStore.items.map(i => i.data),
    )
  }

  @computed get validLocationsForMemberSchedules() {
    const { salonStore } = this.rootStore

    return this.transformLocations(
      undefined,
      this.staticLocations,
      salonStore.mySalonMemberships,
      this.rootStore.externalAddressStore.items.map(i => i.data),
    )
  }

  @computed get canAddBooking() {
    return this.validLocationsWithBookingPermission.length > 0
  }

  /**
   * Whether this account is a salon team member who only uses Solo for managing
   * their own bookings/clients. When true, the business-owner features (booking
   * system, website, marketing tools, payments, stats, Google Business Profile)
   * are hidden, since those are managed at the salon level rather than per-member.
   */
  @computed get salonMemberOnly() {
    return !!this.data.professionalProfile.saasSettings.salonMemberOnly
  }

  checkToMigrateData = async () => {
    // No migration currently needed
  }

  // Create a simplified object for booking locations, with their address details
  transformLocations = (
    mobileLocation: SaasSettingsMobileLocation | undefined,
    staticLocations: SaasSettingsStaticLocation[],
    salonMemberships: SalonMembership[],
    externalAddresses: ExternalAddress[], // Must be passed in so @computed properties run
  ): $BookingLocation[] => {
    const { i18n: { s } } = this.rootStore
    const locations: $BookingLocation[] = []

    const externalAdressesMap = new Map(externalAddresses.map(a => [a._id, a]))
    const addressLine = (externalAddressId?: string | null) => {
      const address = externalAdressesMap.get(externalAddressId || '')
      if (!address) return s('Unknown address')
      return [address.line1, address.line2].filter(Boolean).join(' ')
    }

    if (mobileLocation?.enabled) {
      locations.push({
        _id: MOBILE_AVAILABILITY_ID,
        name: s('Mobile bookings'),
        detail: s('At client address'),
        enabled: mobileLocation.enabled,
      })
    }

    const allEnabledStaticLocations = [
      ...staticLocations,
      ...salonMemberships.flatMap(m => m.locations),
    ].filter(l => l.enabled)

    allEnabledStaticLocations.forEach(location => {
      locations.push({
        _id: location._id,
        name: location.name || s('Salon'),
        detail: addressLine(location.externalAddressId),
        enabled: location.enabled,
        addressId: location.externalAddressId ?? undefined,
      })
    })

    return locations.filter((location, i) => locations.findIndex(l => l._id === location._id) === i)
  }

  // Sync store data
  syncInStorage = (): void => {
    this.rootStore.storeDataSync.saveStoreToStorage('USER')
  }

  openRoadmap = (ideaUrl = '') => {
    const { apiUrl } = this.rootStore.communication.config
    const { _id, emailAddress } = this.data
    let url = `${apiUrl}/feedbackSso?_id=${_id}&emailAddress=${emailAddress}`
    if (ideaUrl) url += `url=${ideaUrl}`

    this.rootStore.uiStore.openLink(url)
  }

  @computed get daysSinceSignUp() {
    return Math.abs(moment().diff(this.data.createdAt, 'days', false))
  }

  fetchLoyaltySchemes = async () => {
    runInAction(() => {
      this.loading = true
    })

    const response = await this.rootStore.communication.requester.getLoyaltySchemes()
    if (response.getLoyaltySchemes) {
      runInAction(() => {
        this.loyaltySchemes = response.getLoyaltySchemes
      })
    }

    runInAction(() => {
      this.loading = false
    })
  }

  @computed get scopedLoyaltySchemes() {
    if (!this.loyaltySchemes) return undefined
    const dataScopeId = this.rootStore.authStore.dataScopeId
    return scopeData(this.loyaltySchemes, dataScopeId ?? this.data._id, 'professionalId')
  }

  @computed get defaultLoyaltyScheme() {
    return this.scopedLoyaltySchemes
      ?.find(s => s.schemeUsage === LoyaltySchemeUsageType.Default || !s.schemeUsage)
  }

  @computed get clientReferralScheme() {
    return this.scopedLoyaltySchemes
      ?.find(s => s.schemeUsage === LoyaltySchemeUsageType.ClientReferral)
  }

  @computed get timezone() {
    const { countryISOCode, timezone } = this.data
    return timezone || countryTimezones[countryISOCode || defaultCountry]
  }

  @computed get framerCountryUrlPrefix() {
    const countryISOCode = this.data.countryISOCode

    const framerCountries: Partial<Record<SupportedCountry, string>> = {
      'IE': 'ie',
      'AU': 'au',
    }

    if (countryISOCode && framerCountries[countryISOCode]) {
      return framerCountries[countryISOCode] + '/'
    }

    return ''
  }

  extendTrial = () => {
    analytics.track('Trial Extension Requested')
    this.rootStore.dropdownMessageHandler.showDropdownMessage({
      title: 'Trial Extension Requested',
      message: 'Our team will be in touch soon.',
      type: 'success',
      closeInterval: 3000,
    })
  }

  openGrowthHubLink = async () => {
    const { userStore, uiStore, config: { growthHubSecretToken, name } } = this.rootStore

    const token = btoa(`${userStore.data._id}:${growthHubSecretToken}`)

    const passEnv = (name === 'production' || !name) ? '' : `&e=${name[0]}`

    uiStore.openLink('https://booksolo.co/my-business/members/home?t=' + token + passEnv)
  }

  dev_resetAccountData = async () => {
    const { uiStore, communication, config: { name } } = this.rootStore

    if (name !== 'development') {
      return
    }

    uiStore.showMessage({
      title: 'Reset account',
      body: 'Are you sure you want to reset this account? This action cannot be undone.',
      buttons: [
        {
          title: 'Reset account data',
          type: 'error',
          onPress: async () => {
            try {
              await communication.requester.resetAccount()

              uiStore.showMessage({
                title: 'Account reset',
                body: 'Account has been reset successfully. You should now refresh the app.',
              })
            } catch {
              uiStore.showMessage({
                title: 'Error',
                body: 'Failed to reset account. Please try again.',
              })
            }
          },
        },
        {
          title: 'Cancel',
          type: 'primary',
        },
      ],
    })
  }
}

export type $UserStore = UserStore